import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import Icon from "./Icon";
import AiMessage from "./AiMessage";
import AiBottomSearchBar from "./AiBottomSearchBar";
import AreaConfirmMap from "./AreaConfirmMap";
import { ENDPOINTS } from "./Constants";
import { uid, authedFetch, withDisclosures } from "./Helpers";

// In-progress onboarding answers only ever live in React state -- nothing
// server-side tracks "stage 3 of 6" until the wizard actually completes
// (personal_agent/service.py is deliberately stateless in between), so a
// refresh mid-wizard would silently throw every answer away and restart at
// stage 1. Saving the draft here on every stage change, keyed per account,
// lets a reload on the same device pick back up instead.
const draftKey = (email) => `ogm_personal_agent_draft_${email}`;

function loadDraft(email) {
  if (!email) return null;
  try {
    const raw = localStorage.getItem(draftKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.stage?.index ? parsed : null;
  } catch {
    return null;
  }
}

function saveDraft(email, stage, profile) {
  if (!email) return;
  try {
    localStorage.setItem(draftKey(email), JSON.stringify({ stage, profile }));
  } catch {
    // Private browsing / storage full -- the wizard still works, it just
    // won't survive a refresh this time.
  }
}

function clearDraft(email) {
  if (!email) return;
  try {
    localStorage.removeItem(draftKey(email));
  } catch { /* nothing to clean up if this fails */ }
}

// Exported so PersonalAgentPage.jsx's "Start over" can clear a leftover
// draft using the exact same key convention, rather than re-deriving
// `ogm_personal_agent_draft_${email}` a second time somewhere else and
// risking the two silently drifting apart.
export { clearDraft };

const GuidedSearchContext = createContext(null);

function useGuidedSearchContext() {
  const ctx = useContext(GuidedSearchContext);
  if (!ctx) throw new Error("GuidedSearchBody/GuidedSearchComposer must be rendered inside <GuidedSearch>");
  return ctx;
}

/**
 * The 6-question onboarding wizard's state machine -- stage rendering,
 * chip/free-text answers, the localStorage draft, the post-stage-6
 * confirmation gate (with a free-text correction box), and, once confirmed,
 * the resulting chat (results + refinement composer). Presentation-agnostic:
 * this component only provides state via context; <GuidedSearchBody/> and
 * <GuidedSearchComposer/> render it. Split into two render pieces (rather
 * than one) because a host page's chat composer is typically a flex sibling
 * of the scrolling message area, not a descendant of it (see
 * PersonalAgentPage.jsx) -- pinning it via CSS while nesting it would either
 * scroll it away or require reworking layout that already works.
 *
 * `initialMessages`/`initialChatId` let a host resume an already-completed
 * conversation (e.g. PersonalAgentPage restoring a saved chat) without
 * re-running the wizard -- when non-empty, the wizard/confirmation steps are
 * skipped entirely and this mounts straight into chat mode. `initialAnswer`
 * lets a host (e.g. the homepage overlay) hand off text already typed
 * before the wizard opened -- applied as stage 1's answer once a fresh
 * stage 1 loads, rather than discarding it.
 */
export default function GuidedSearch({
  token,
  email,
  initialMessages = [],
  initialChatId = null,
  initialProfile = {},
  initialAnswer = "",
  initialSubscribed = false,
  onMessagesChange,
  onChatIdChange,
  onCompleted,
  onNoResults,
  onSubscribedChange,
  children,
}) {
  const [stage, setStage] = useState(null);
  // Seeded from any preferences already known about the user (e.g. from
  // casual Ask AI conversation, tracked independently of onboarding
  // completion) -- a local draft, if one exists, takes precedence below.
  const [profile, setProfile] = useState(initialProfile);
  const [summary, setSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [refining, setRefining] = useState(false);
  const [error, setError] = useState("");
  // Stage 2 only: holds the next stage back until the user explicitly
  // confirms the geocoded area pins (or retypes the answer, which resets
  // both back to null via a fresh submitAnswer call).
  const [areaPins, setAreaPins] = useState(null);
  const [pendingNextStage, setPendingNextStage] = useState(null);
  const [messages, setMessagesState] = useState(initialMessages);
  const [chatId, setChatIdState] = useState(initialChatId);
  // Guided-search plan D12/R2: a confirmed search is ephemeral by default --
  // this only ever flips true via an explicit POST /subscribe (never as a
  // side effect of confirming), and back to false via /pause -- the same
  // action the daily email's one-click unsubscribe link and
  // PersonalAgentPage's own pause button both trigger, so this toggle
  // always reflects the one real subscription state (R1: no UI dead end).
  const [subscribed, setSubscribed] = useState(initialSubscribed);
  const [subscribing, setSubscribing] = useState(false);

  // One ref per async action below, each mirroring that action's own
  // `submitting`/`correcting`/etc. state guard. The state guard alone isn't
  // enough: two synchronous invocations in the same tick (a fast double-
  // click, or a chip clicked while Enter is also mid-press) both read the
  // same stale "not busy" value, since a state update isn't visible to a
  // second call until React re-renders -- found live in AiSearchPage.js's
  // retryMessage, which had the identical shape. A ref mutates immediately,
  // so the second call sees the lock regardless of render timing.
  const submittingRef  = useRef(false);
  const correctingRef  = useRef(false);
  const confirmingRef  = useRef(false);
  const refiningRef    = useRef(false);
  const subscribingRef = useRef(false);

  const setMessages = useCallback((updater) => {
    setMessagesState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      onMessagesChange?.(next);
      return next;
    });
  }, [onMessagesChange]);

  const setChatId = useCallback((value) => {
    setChatIdState(value);
    onChatIdChange?.(value);
  }, [onChatIdChange]);

  // Bootstraps once on mount: resumes a local draft, starts a fresh wizard,
  // or (when the host already handed us a completed chat) does nothing --
  // deliberately not re-run on prop changes, a host mounts one GuidedSearch
  // per conversation.
  useEffect(() => {
    if (initialMessages?.length) return;
    const draft = loadDraft(email);
    if (draft) {
      setStage(draft.stage);
      setProfile(draft.profile || {});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const first = await authedFetch(ENDPOINTS.PERSONAL_AGENT_START, token, { method: "POST" });
        if (!cancelled) setStage(first);
      } catch (err) {
        if (!cancelled) setError(err.message || "Couldn't start your Personal Agent.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persists onboarding progress on every stage advance so a refresh can
  // resume instead of restarting. Only runs while the wizard is actually
  // showing (stage set, not yet at the confirmation gate).
  useEffect(() => {
    if (!stage || !email) return;
    saveDraft(email, stage, profile);
  }, [stage, profile, email]);

  const submitAnswer = useCallback(async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || !stage || submitting || submittingRef.current) return;
    submittingRef.current = true;

    setSubmitting(true);
    setError("");
    // A fresh submission (retyping after a rejected or unconfirmed answer)
    // clears any leftover confirm-gate state from the previous attempt.
    setAreaPins(null);
    setPendingNextStage(null);
    try {
      const result = await authedFetch(ENDPOINTS.PERSONAL_AGENT_ANSWER, token, {
        method: "POST",
        body: JSON.stringify({ stage: stage.index, answer: text, profile }),
      });
      setProfile(result.profile || profile);
      // retryHint means the backend rejected the answer and nextStage is
      // this SAME stage again (submit_answer's own contract, currently
      // only for stage 2) -- surface it via the wizard's existing error
      // paragraph rather than silently re-rendering an identical question.
      if (result.retryHint) {
        setError(result.retryHint);
      }
      if (result.completed) {
        setStage(null);
        clearDraft(email);
        // Blocking confirmation gate -- no search has run yet, see
        // router_personal_agent.py's /confirm.
        setSummary(result.summary || { groups: [] });
      } else if (!result.retryHint && result.areaPins?.length > 0) {
        // Stage 2 succeeded and geocoded to at least one real pin -- hold
        // here instead of silently advancing; confirmAreaPins() (below)
        // is what actually moves to nextStage once the user taps Confirm.
        setAreaPins(result.areaPins);
        setPendingNextStage(result.nextStage);
      } else {
        setStage(result.nextStage);
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  }, [stage, submitting, profile, token, email]);

  const confirmAreaPins = useCallback(() => {
    if (!pendingNextStage) return;
    setStage(pendingNextStage);
    setAreaPins(null);
    setPendingNextStage(null);
  }, [pendingNextStage]);

  // A homepage overlay may hand off text the person already typed before
  // the wizard opened (see the guided-search plan's D2/homepage-interception)
  // -- rather than discarding it, apply it as stage 1's answer as soon as a
  // fresh stage 1 loads, landing the person straight on stage 2. Guarded by
  // a ref (not state) so it fires exactly once, whether stage 1 arrives from
  // /start or, via strict-mode's double-invoke, a second bootstrap pass.
  const initialAnswerAppliedRef = useRef(false);
  useEffect(() => {
    if (initialAnswerAppliedRef.current) return;
    if (!initialAnswer?.trim() || stage?.index !== 1) return;
    initialAnswerAppliedRef.current = true;
    submitAnswer(initialAnswer);
  }, [stage, initialAnswer, submitAnswer]);

  const submitCorrection = useCallback(async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || correcting || correctingRef.current) return;
    correctingRef.current = true;

    setCorrecting(true);
    setError("");
    try {
      const result = await authedFetch(ENDPOINTS.PERSONAL_AGENT_CORRECT, token, {
        method: "POST",
        body: JSON.stringify({ profile, correction: text }),
      });
      setProfile(result.profile);
      setSummary(result.summary);
    } catch (err) {
      setError(err.message || "Couldn't apply that correction. Please try again.");
    } finally {
      setCorrecting(false);
      correctingRef.current = false;
    }
  }, [profile, correcting, token]);

  // Drops one value that came from saved preferences (not from this wizard's answers).
  const removePrefilled = useCallback(async (key) => {
    if (correcting || correctingRef.current) return;
    correctingRef.current = true;
    setCorrecting(true);
    setError("");
    try {
      const result = await authedFetch(ENDPOINTS.PERSONAL_AGENT_REMOVE_PREFILLED, token, {
        method: "POST",
        body: JSON.stringify({ profile, key }),
      });
      setProfile(result.profile);
      setSummary(result.summary);
    } catch (err) {
      setError(err.message || "Couldn't remove that. Please try again.");
    } finally {
      setCorrecting(false);
      correctingRef.current = false;
    }
  }, [profile, correcting, token]);

  const confirmSearch = useCallback(async () => {
    if (confirming || confirmingRef.current) return;
    confirmingRef.current = true;

    setConfirming(true);
    setError("");
    try {
      const result = await authedFetch(ENDPOINTS.PERSONAL_AGENT_CONFIRM, token, {
        method: "POST",
        body: JSON.stringify({ profile }),
      });
      setChatId(result.chatId);
      setSummary(null);

      const found = result.results?.hasResults;
      // Always surface a message, even on zero results -- setMessages only
      // running when `found` left `messages` empty alongside a cleared
      // `summary` and no `stage`, which the mode ternary below has no
      // branch for; it fell through to the "loading" default and got stuck
      // on "Loading your Personal Agent…" forever despite the search having
      // actually finished. Matches searchNow's own fix for this exact
      // failure shape (PersonalAgentPage.jsx).
      setMessages([{
        id: uid(),
        role: "ai",
        text: withDisclosures(result.results?.message || "No new matches right now — check back later.", result.results?.requirementDisclosures),
        hasResults: found || false,
        properties: result.results?.properties || [],
        followUps: result.results?.followUps || [],
        relaxationOptions: result.results?.relaxationOptions || [],
        timestamp: Date.now(),
        // AiMessage normally hides bullet lists in a property-bearing AI
        // reply (.msg-text-intro, ai-chatbox.css) to avoid the model
        // re-listing properties in prose above the cards. Here the
        // bullets are the confirmed-preferences recap, not a property
        // re-listing, so keep them visible.
        keepIntroList: true,
      }]);
      onCompleted?.(profile);
      if (!found) onNoResults?.();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setConfirming(false);
      confirmingRef.current = false;
    }
  }, [confirming, profile, token, onCompleted, onNoResults, setChatId, setMessages]);

  const submitRefinement = useCallback(async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || !chatId || refining || refiningRef.current) return;
    refiningRef.current = true;

    setRefining(true);
    setError("");
    setMessages((prev) => [...prev, { id: uid(), role: "user", text, timestamp: Date.now() }]);
    try {
      // Reuses the existing Ask AI endpoint -- ConversationContext.merge_with_memory
      // narrows this same search server-side rather than starting a new one.
      const result = await authedFetch(ENDPOINTS.AI_ASK, token, {
        method: "POST",
        body: JSON.stringify({ question: text, chatId }),
      });
      setMessages((prev) => [...prev, {
        id: uid(),
        role: "ai",
        text: withDisclosures(result.message || "", result.requirementDisclosures, result.excludedByFixed, result.eligibleCount),
        hasResults: result.hasResults,
        properties: result.properties,
        followUps: result.followUps || [],
        relaxationOptions: result.relaxationOptions || [],
        timestamp: Date.now(),
      }]);
    } catch (err) {
      setError(err.message || "Couldn't refine your search. Please try again.");
    } finally {
      setRefining(false);
      refiningRef.current = false;
    }
  }, [chatId, refining, token, setMessages]);

  // Turns a relaxation chip (Sheet 11 / Sprint 2.4) from informational into
  // an explicit, user-chosen action -- never applied automatically. Reuses
  // the same "append user + ai turn" shape submitRefinement uses, so a
  // relaxation reads in the transcript like any other thing the user asked
  // for, not a hidden system action.
  const applyRelaxation = useCallback(async (dimension, label, sourceMessageId) => {
    if (!chatId || refining || refiningRef.current) return;
    refiningRef.current = true;

    setRefining(true);
    setError("");
    // Marks the chip's own message as "used" immediately -- not just after
    // the response comes back -- so (a) the click gives instant feedback
    // instead of looking like nothing happened while the request is in
    // flight, and (b) that same historical message's chips can't be
    // clicked again later and append a duplicate result. A later, NEW
    // zero-result message still gets its own live chips.
    setMessages((prev) => prev.map((m) => (m.id === sourceMessageId ? { ...m, relaxationApplied: dimension } : m)));
    setMessages((prev) => [...prev, { id: uid(), role: "user", text: label, timestamp: Date.now() }]);
    try {
      const result = await authedFetch(ENDPOINTS.PERSONAL_AGENT_APPLY_RELAXATION, token, {
        method: "POST",
        body: JSON.stringify({ chatId, dimension }),
      });
      setMessages((prev) => [...prev, {
        id: uid(),
        role: "ai",
        text: withDisclosures(result.results?.message || "", result.results?.requirementDisclosures,
            result.results?.excludedByFixed, result.results?.eligibleCount),
        hasResults: result.results?.hasResults || false,
        properties: result.results?.properties || [],
        followUps: result.results?.followUps || [],
        relaxationOptions: result.results?.relaxationOptions || [],
        timestamp: Date.now(),
      }]);
    } catch (err) {
      setError(err.message || "Couldn't apply that change. Please try again.");
      // Failed, not used -- let them try the same chip again.
      setMessages((prev) => prev.map((m) => (m.id === sourceMessageId ? { ...m, relaxationApplied: null } : m)));
    } finally {
      setRefining(false);
      refiningRef.current = false;
    }
  }, [chatId, refining, token, setMessages]);

  const handleCopy = useCallback((text) => {
    navigator.clipboard?.writeText(text || "");
  }, []);

  const toggleSubscription = useCallback(async () => {
    if (subscribing || subscribingRef.current) return;
    subscribingRef.current = true;
    const next = !subscribed;

    setSubscribing(true);
    setError("");
    try {
      if (next) {
        await authedFetch(ENDPOINTS.PERSONAL_AGENT_SUBSCRIBE, token, {
          method: "POST",
          body: JSON.stringify({ profile }),
        });
      } else {
        await authedFetch(ENDPOINTS.PERSONAL_AGENT_PAUSE, token, { method: "POST" });
      }
      setSubscribed(next);
      onSubscribedChange?.(next);
    } catch (err) {
      setError(err.message || "Couldn't update your email preference. Please try again.");
    } finally {
      setSubscribing(false);
      subscribingRef.current = false;
    }
  }, [subscribed, subscribing, profile, token, onSubscribedChange]);

  const mode = messages.length > 0 ? "chat" : summary ? "confirm" : stage ? "wizard" : "loading";

  const value = {
    mode, stage, profile, summary, submitting, confirming, correcting, refining, error,
    messages, chatId, subscribed, subscribing, areaPins, pendingNextStage,
    submitAnswer, submitCorrection, removePrefilled, confirmSearch, submitRefinement, applyRelaxation, handleCopy, toggleSubscription,
    confirmAreaPins,
  };

  return (
    <GuidedSearchContext.Provider value={value}>
      {children ?? (<><GuidedSearchBody /><GuidedSearchComposer /></>)}
    </GuidedSearchContext.Provider>
  );
}

/** The scrollable part: wizard question, confirmation summary, or chat messages. */
export function GuidedSearchBody() {
  const {
    mode, stage, summary, submitting, confirming, correcting, error,
    messages, chatId, refining, submitAnswer, submitCorrection, confirmSearch, handleCopy, submitRefinement,
    applyRelaxation, areaPins, confirmAreaPins, removePrefilled,
  } = useGuidedSearchContext();

  if (mode === "loading") {
    return <div className="pa-loading">Loading your Personal Agent…</div>;
  }

  if (mode === "wizard") {
    return (
      <div className="pa-content">
        <div className="pa-wizard">
          <div className="pa-avatar"><Icon name="sparkle" size={26} /></div>
          <span className="pa-stage-label">Stage {stage.index} of {stage.total}</span>
          <h2 className="pa-question">{stage.question}</h2>
          {stage.index === 1 && (
            <p className="pa-intro">
              We'll ask a few questions about the home you're looking for, then search for you every day.
            </p>
          )}

          <div className="pa-progress-track">
            <div className="pa-progress-fill" style={{ width: `${(stage.index / stage.total) * 100}%` }} />
          </div>

          {stage.index === 2 && areaPins?.length > 0 && (
            <div className="pa-area-confirm">
              <AreaConfirmMap pins={areaPins} />
              <p className="pa-area-confirm-label">
                Is <strong>{areaPins.map((p) => p.name).join(", ")}</strong> right?
              </p>
              <button type="button" className="pa-area-confirm-btn" onClick={confirmAreaPins}>
                Confirm
              </button>
            </div>
          )}

          {stage.inputType === "select" && stage.options?.length > 0 && (
            <div className="pa-chip-row" role="group" aria-label="Suggested answers">
              {stage.options.map((option) => (
                <button
                  key={option}
                  type="button"
                  className="pa-chip"
                  onClick={() => submitAnswer(option)}
                  disabled={submitting}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          <div className="pa-bar-wrap">
            <AiBottomSearchBar
              key={stage.index}
              onSend={submitAnswer}
              placeholder={stage.placeholder}
              busy={submitting}
            />
          </div>

          {submitting && (
            <div className="pa-thinking" role="status" aria-label="Finding your answer">
              <span className="typing-indicator">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </span>
              Thinking…
            </div>
          )}

          {error && <p className="pa-error">{error}</p>}
        </div>
      </div>
    );
  }

  if (mode === "confirm") {
    return (
      <div className="pa-content">
        <div className="pa-wizard">
          <div className="pa-avatar"><Icon name="sparkle" size={26} /></div>
          <span className="pa-stage-label">Here's what I heard</span>
          <h2 className="pa-question">Does this look right?</h2>

          {summary.groups.some((g) => g.prefilled?.length) && (
            <p className="pa-prefill-note">
              Items tagged <span className="pa-prefill-tag">saved</span> come from your earlier activity,
              not this conversation. Remove any that don't apply.
            </p>
          )}

          <ul className="pa-summary-list">
            {summary.groups.map((group) => (
              <li key={group.field} className="pa-summary-item">
                <span className="pa-summary-label">{group.label}</span>
                <span className="pa-summary-value">
                  {group.value}
                  {group.prefilled?.length > 0 && (
                    <span className="pa-prefill-list">
                      {group.prefilled.map((item) => (
                        <span key={item.key} className="pa-prefill-chip">
                          <span className="pa-prefill-tag">saved</span>
                          {item.label}
                          <button
                            type="button"
                            className="pa-prefill-remove"
                            aria-label={`Remove ${item.label}`}
                            onClick={() => removePrefilled(item.key)}
                            disabled={correcting || confirming}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          <div className="pa-confirm-actions">
            <button
              type="button"
              className="pa-confirm-btn"
              onClick={confirmSearch}
              disabled={confirming || correcting}
            >
              {confirming ? "Searching…" : "Search"}
            </button>
          </div>

          <div className="pa-correction-row">
            <AiBottomSearchBar
              onSend={submitCorrection}
              placeholder="Something not right? Tell me what to change"
              busy={correcting || confirming}
            />
          </div>

          {(confirming || correcting) && (
            <div className="pa-thinking" role="status" aria-label="Updating">
              <span className="typing-indicator">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </span>
              {confirming ? "Searching…" : "Updating…"}
            </div>
          )}

          {error && <p className="pa-error">{error}</p>}
        </div>
      </div>
    );
  }

  // mode === "chat"
  return (
    <div className="chat-messages-container">
      {messages.map((msg, i) => (
        <AiMessage
          key={msg.id}
          msg={msg}
          chatId={chatId}
          index={i}
          onCopy={handleCopy}
          onFollowUp={submitRefinement}
          onApplyRelaxation={applyRelaxation}
          prevMessages={messages.slice(0, i)}
          compareList={[]}
          engagementSource="personal_agent"
        />
      ))}
      {refining && (
        <div className="message-row ai" role="status" aria-label="AI is thinking">
          <div className="typing-indicator">
            <div className="typing-dot" />
            <div className="typing-dot" />
            <div className="typing-dot" />
          </div>
        </div>
      )}
      {/* Chat mode had no error rendering at all -- a failed submitRefinement
          or applyRelaxation call reverted its own loading state but had no
          way to tell the user why nothing happened (found while fixing the
          duplicate-relaxation-chip bug: a stale/expired chat context 400s,
          and that 400 was previously invisible). */}
      {error && <p className="pa-error pa-error--chat">{error}</p>}
    </div>
  );
}

/** The pinned refinement composer -- renders only once a chat exists, so a
 * host can mount it unconditionally as a flex sibling of the scroll area.
 * Also carries the post-results subscribe toggle (D12/R1) -- pinned rather
 * than inline in the scrolling messages so it never scrolls out of reach
 * as a conversation grows. */
export function GuidedSearchComposer() {
  const { mode, refining, submitRefinement, subscribed, subscribing, toggleSubscription } = useGuidedSearchContext();
  if (mode !== "chat") return null;

  return (
    <div className="chat-input-bar">
      <label className="pa-subscribe-toggle">
        <input type="checkbox" checked={subscribed} disabled={subscribing} onChange={toggleSubscription} />
        Email me when new matches like this appear
      </label>
      <AiBottomSearchBar
        onSend={submitRefinement}
        placeholder="Refine your search — e.g. only under ₹1.5 Cr, ready to move only"
        busy={refining}
      />
    </div>
  );
}
